import {
  getGetViewerQueryKey,
  useLoginUser,
  useRegisterUser,
} from "@/api/generated/hooks/auth/auth";
import type { SetupAuthResponse } from "@/api/generated/types";
import { isApiError } from "@/api/lib/fetcher";
import { Header } from "@/components/features/layout/header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { persistAuthToken, setStoredAuthToken } from "@/lib/auth-token";
import { useViewerQuery } from "@/lib/viewer";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, BrainCircuit, Building2, KeyRound, Lock, Mail, UserRound } from "lucide-react";
import { type FormEvent, type HTMLInputTypeAttribute, useEffect, useState } from "react";

type AuthMode = "login" | "signup";

type AuthFieldProps = {
  autoComplete?: string;
  description?: string;
  icon: typeof Mail;
  id: string;
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  value: string;
};

type LoginViewProps = {
  email: string;
  formError: string | null;
  isPending: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (checked: boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSwitchToSignup: () => void;
  password: string;
  rememberMe: boolean;
};

type SignupViewProps = {
  company: string;
  email: string;
  formError: string | null;
  isPending: boolean;
  name: string;
  onCompanyChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSwitchToLogin: () => void;
  onTermsAcceptedChange: (checked: boolean) => void;
  password: string;
  termsAccepted: boolean;
};

function readErrorMessage(error: Error | null): string | null {
  if (!error) {
    return null;
  }

  if (isApiError(error)) {
    return error.message;
  }

  return error.message;
}

function AuthField({
  autoComplete,
  description,
  icon: Icon,
  id,
  label,
  onChange,
  placeholder,
  required,
  type = "text",
  value,
}: AuthFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-bold text-slate-700" htmlFor={id}>
        {label}
      </Label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <Input
          autoComplete={autoComplete}
          className="h-11 rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 text-sm placeholder:text-slate-400 focus-visible:ring-primary"
          id={id}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          required={required}
          type={type}
          value={value}
        />
      </div>
      {description ? <p className="text-xs text-slate-500">{description}</p> : null}
    </div>
  );
}

function FormErrorMessage({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <p
      aria-live="polite"
      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
    >
      {message}
    </p>
  );
}

function LoginView({
  email,
  formError,
  isPending,
  onEmailChange,
  onPasswordChange,
  onRememberMeChange,
  onSubmit,
  onSwitchToSignup,
  password,
  rememberMe,
}: LoginViewProps) {
  return (
    <div className="flex min-h-screen flex-col bg-[#f9fafb]">
      <Header mode="public" />
      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <section className="rounded-2xl border border-slate-100 bg-white px-10 py-8 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05),0_2px_4px_-1px_rgba(0,0,0,0.03)]">
            <div className="mb-8 text-center">
              <h2 className="text-xl font-bold text-slate-800">採用コンソールへログイン</h2>
              <p className="mt-2 text-xs text-slate-400">
                アカウント情報を入力して続行してください
              </p>
            </div>
            <form className="space-y-6" onSubmit={onSubmit}>
              <AuthField
                autoComplete="email"
                icon={Mail}
                id="email"
                label="メールアドレス"
                onChange={onEmailChange}
                placeholder="name@company.com"
                required
                type="email"
                value={email}
              />
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-bold text-slate-700" htmlFor="password">
                    パスワード
                  </Label>
                  <button
                    className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
                    type="button"
                  >
                    パスワードをお忘れですか？
                  </button>
                </div>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                    <Lock className="size-4" aria-hidden="true" />
                  </span>
                  <Input
                    autoComplete="current-password"
                    className="h-11 rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 text-sm placeholder:text-slate-400 focus-visible:ring-primary"
                    id="password"
                    onChange={(event) => onPasswordChange(event.target.value)}
                    placeholder="••••••••"
                    required
                    type="password"
                    value={password}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={rememberMe}
                  className="size-4 rounded border-slate-300 data-[state=checked]:bg-primary"
                  id="remember-me"
                  onCheckedChange={(checked) => onRememberMeChange(checked === true)}
                />
                <Label className="ml-2 text-sm font-normal text-slate-600" htmlFor="remember-me">
                  次回から自動ログイン
                </Label>
              </div>
              <FormErrorMessage message={formError} />
              <Button
                className="h-11 w-full rounded-lg text-sm font-bold shadow-sm"
                disabled={isPending}
                type="submit"
              >
                {isPending ? "送信中..." : "ログイン"}
              </Button>
            </form>
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-xs text-slate-500">その他のオプション</span>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  className="h-11 rounded-lg border-slate-300 bg-white font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  onClick={onSwitchToSignup}
                  type="button"
                  variant="outline"
                >
                  <UserRound className="size-4 text-slate-500" aria-hidden="true" />
                  新規登録
                </Button>
                <Button
                  className="h-11 rounded-lg border-slate-300 bg-white font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  type="button"
                  variant="outline"
                >
                  <KeyRound className="size-4 text-slate-500" aria-hidden="true" />
                  SSOログイン
                </Button>
              </div>
            </div>
          </section>
          <footer className="text-center">
            <div className="flex justify-center gap-6 text-xs text-slate-500">
              <button className="transition-colors hover:text-primary" type="button">
                利用規約
              </button>
              <span className="text-slate-300">|</span>
              <button className="transition-colors hover:text-primary" type="button">
                プライバシーポリシー
              </button>
              <span className="text-slate-300">|</span>
              <button className="transition-colors hover:text-primary" type="button">
                ヘルプ
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-400">
              © 2024 Relance AI面談. All rights reserved.
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}

function SignupView({
  company,
  email,
  formError,
  isPending,
  name,
  onCompanyChange,
  onEmailChange,
  onNameChange,
  onPasswordChange,
  onSubmit,
  onSwitchToLogin,
  onTermsAcceptedChange,
  password,
  termsAccepted,
}: SignupViewProps) {
  const isSubmitDisabled = isPending || !termsAccepted;

  return (
    <div className="flex min-h-screen flex-col bg-[#f9fafb]">
      <Header mode="public" />
      <main className="flex flex-1 items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
            <div className="px-8 py-8 md:px-10 md:py-10">
              <div className="mb-8 flex flex-col items-center text-center">
                <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-[#004d50] text-white shadow-lg shadow-primary/20">
                  <BrainCircuit className="size-7" aria-hidden="true" />
                </div>
                <h1 className="mb-2 text-2xl font-bold text-slate-900">新規アカウント登録</h1>
                <p className="text-sm text-slate-500">
                  AI採用アシスタントで、
                  <br className="hidden sm:block" />
                  より効率的な採用活動を始めましょう。
                </p>
              </div>
              <form className="flex flex-col gap-5" onSubmit={onSubmit}>
                <AuthField
                  autoComplete="organization"
                  icon={Building2}
                  id="company"
                  label="会社名"
                  onChange={onCompanyChange}
                  placeholder="株式会社Example"
                  value={company}
                />
                <AuthField
                  autoComplete="name"
                  icon={UserRound}
                  id="name"
                  label="氏名"
                  onChange={onNameChange}
                  placeholder="山田 太郎"
                  required
                  value={name}
                />
                <AuthField
                  autoComplete="email"
                  icon={Mail}
                  id="signup-email"
                  label="仕事用メールアドレス"
                  onChange={onEmailChange}
                  placeholder="taro.yamada@example.co.jp"
                  required
                  type="email"
                  value={email}
                />
                <AuthField
                  autoComplete="new-password"
                  description="少なくとも8文字以上、大文字と数字を含めてください。"
                  icon={Lock}
                  id="signup-password"
                  label="パスワード"
                  onChange={onPasswordChange}
                  placeholder="8文字以上の英数字"
                  required
                  type="password"
                  value={password}
                />
                <div className="mt-1 flex items-start gap-3">
                  <Checkbox
                    checked={termsAccepted}
                    className="mt-0.5 size-4 rounded border-slate-300 data-[state=checked]:bg-primary"
                    id="terms"
                    onCheckedChange={(checked) => onTermsAcceptedChange(checked === true)}
                  />
                  <Label className="text-sm font-medium leading-6 text-slate-600" htmlFor="terms">
                    <span className="text-primary">利用規約</span> と{" "}
                    <span className="text-primary">プライバシーポリシー</span> に同意します。
                  </Label>
                </div>
                <FormErrorMessage message={formError} />
                <Button
                  className="mt-1 h-12 rounded-lg text-sm font-bold shadow-lg shadow-primary/25"
                  disabled={isSubmitDisabled}
                  type="submit"
                >
                  <span>{isPending ? "送信中..." : "無料でトライアルを開始"}</span>
                  {!isPending ? <ArrowRight className="size-4" aria-hidden="true" /> : null}
                </Button>
              </form>
              <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                <p className="text-sm text-slate-500">既にアカウントをお持ちの方はこちら</p>
                <button
                  className="mt-2 inline-block font-bold text-primary hover:underline"
                  onClick={onSwitchToLogin}
                  type="button"
                >
                  ログイン画面へ移動
                </button>
              </div>
            </div>
            <div className="h-1.5 w-full bg-gradient-to-r from-primary via-[#008f94] to-primary" />
          </section>
          <footer className="mt-8 flex justify-center gap-6 text-xs text-slate-400">
            <button className="transition-colors hover:text-slate-600" type="button">
              ヘルプ
            </button>
            <button className="transition-colors hover:text-slate-600" type="button">
              お問い合わせ
            </button>
            <span className="text-slate-300">© 2024 Relance AI面談.</span>
          </footer>
        </div>
      </main>
    </div>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const viewerQuery = useViewerQuery();
  const loginMutation = useLoginUser();
  const registerMutation = useRegisterUser();
  const [mode, setMode] = useState<AuthMode>("login");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (viewerQuery.data?.role === "operator") {
      void navigate({ to: "/candidates" });
    }
  }, [navigate, viewerQuery.data]);

  useEffect(() => {
    setFormError(null);
  }, [mode]);

  const isPending = loginMutation.isPending || registerMutation.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (mode === "signup" && !termsAccepted) {
      setFormError("利用規約とプライバシーポリシーへの同意が必要です。");
      return;
    }

    try {
      const response =
        mode === "login"
          ? await loginMutation.mutateAsync({
              data: {
                email,
                password,
              },
            })
          : await registerMutation.mutateAsync({
              data: {
                name,
                email,
                password,
              },
            });

      if (response.status !== 200) {
        throw new Error("認証に失敗しました。");
      }

      const body = response.data as SetupAuthResponse;
      persistAuthToken({ headers: response.headers, body });
      await queryClient.invalidateQueries({ queryKey: getGetViewerQueryKey() });

      if (body.role !== "operator") {
        setStoredAuthToken(null);
        setFormError("管理画面は operator アカウントのみ利用できます。");
        return;
      }

      await navigate({ to: "/candidates" });
    } catch (error) {
      setFormError(
        readErrorMessage(error instanceof Error ? error : new Error("ログインに失敗しました"))
      );
    }
  }

  if (mode === "signup") {
    return (
      <SignupView
        company={company}
        email={email}
        formError={formError}
        isPending={isPending}
        name={name}
        onCompanyChange={setCompany}
        onEmailChange={setEmail}
        onNameChange={setName}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
        onSwitchToLogin={() => setMode("login")}
        onTermsAcceptedChange={setTermsAccepted}
        password={password}
        termsAccepted={termsAccepted}
      />
    );
  }

  return (
    <LoginView
      email={email}
      formError={formError}
      isPending={isPending}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onRememberMeChange={setRememberMe}
      onSubmit={handleSubmit}
      onSwitchToSignup={() => setMode("signup")}
      password={password}
      rememberMe={rememberMe}
    />
  );
}
